import { ArrowDown, ArrowUp, ArrowUpDown, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface MobileSortOption {
  id: string;
  label: string;
  /** Optional override for the descending-direction label (e.g. "Newest first"). Falls back to "Descending". */
  descLabel?: string;
  /** Optional override for the ascending-direction label (e.g. "Oldest first"). Falls back to "Ascending". */
  ascLabel?: string;
}

export interface MobileSortValue {
  id: string;
  desc: boolean;
}

interface MobileSortControlProps {
  options: MobileSortOption[];
  value?: MobileSortValue;
  onChange: (value: MobileSortValue) => void;
  className?: string;
  /** Default direction when selecting a new (not currently selected) column. */
  defaultDesc?: boolean;
}

export function computeNextSort(
  optionId: string,
  current: MobileSortValue | undefined,
  defaultDesc: boolean,
): MobileSortValue {
  if (current?.id === optionId) {
    return { id: optionId, desc: !current.desc };
  }
  return { id: optionId, desc: defaultDesc };
}

export function MobileSortControl({
  options,
  value,
  onChange,
  className,
  defaultDesc = true,
}: MobileSortControlProps) {
  const current = value ? options.find((opt) => opt.id === value.id) : undefined;
  const directionLabel = (() => {
    if (!value || !current) return '';
    return value.desc ? (current.descLabel ?? 'Descending') : (current.ascLabel ?? 'Ascending');
  })();
  const DirectionIcon = value ? (value.desc ? ArrowDown : ArrowUp) : ArrowUpDown;

  const handleSelect = (optionId: string) => {
    onChange(computeNextSort(optionId, value, defaultDesc));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={className ?? 'h-10 w-full justify-between text-[14px]'}
          aria-label="Sort"
        >
          <span className="truncate">
            Sort: {current ? current.label : 'Default'}
            {value ? <span className="text-muted-foreground"> · {directionLabel}</span> : null}
          </span>
          <DirectionIcon className="ml-2 h-4 w-4 shrink-0 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
        <DropdownMenuLabel>Sort by</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((option) => {
          const isActive = value?.id === option.id;
          return (
            <DropdownMenuItem
              key={option.id}
              onSelect={(event) => {
                event.preventDefault();
                handleSelect(option.id);
              }}
              className="flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2">
                {isActive ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                {option.label}
              </span>
              {isActive ? (
                value?.desc ? (
                  <ArrowDown className="h-4 w-4 opacity-70" />
                ) : (
                  <ArrowUp className="h-4 w-4 opacity-70" />
                )
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
